/**
 * Enter a fixture.
 *
 * The kind and the config both live in the URL rather than in client state:
 * they ask for entirely different things, and "enter a T20" should be a link
 * somebody can bookmark or send. That was true under Next and is the reason the
 * query string is read here rather than held in state.
 */

import { Link, useParams, useSearchParams } from "react-router-dom";

import { ChoicePicker } from "@/components/fixtures/ChoicePicker";
import { FixtureForm } from "@/components/fixtures/FixtureForm";
import { Button, Crumbs, PageHead, Problem } from "@/components/kit";
import { getFixtureForm } from "@/lib/api/client";
import { displayName } from "@/lib/format";
import { useApi } from "@/spa/useApi";

export function NewFixturePage() {
  const { sport = "" } = useParams<{ sport: string }>();
  const [search] = useSearchParams();
  const type = search.get("type") ?? undefined;
  const config = search.get("config") ?? undefined;

  const { data: context, loading, error } = useApi(
    () => getFixtureForm(sport, type, config),
    [sport, type, config],
    "failed to load",
  );

  if (error) return <Problem>Could not load the fixture form: {error}</Problem>;
  if (!context) return <PageHead title="New fixture" note={loading ? "Loading…" : ""} />;

  // Nothing to choose when the sport has exactly one way of entering a fixture;
  // the service has already resolved it rather than asking a question with one
  // answer.
  const choosing = context.typeDef === null;
  const chosen = context.choices.find(
    (c) => c.fixtureType === context.fixtureType && c.config === context.config,
  );

  return (
    <>
      <Crumbs
        trail={[
          { label: "Sports", href: "/sports" },
          { label: displayName(sport), href: `/${sport}` },
          { label: "Fixtures", href: `/${sport}/fixtures` },
          { label: chosen?.label ?? "New" },
        ]}
      />
      <PageHead
        title={
          choosing
            ? `What kind of ${displayName(sport)} fixture?`
            : `Enter a ${(chosen?.label ?? context.typeDef?.label ?? "fixture").toLowerCase()}`
        }
        note={
          choosing
            ? "This sport holds more than one, and they ask for different things."
            : "Every field below comes from this sport's own definition."
        }
      />

      {choosing ? (
        <ChoicePicker sport={sport} choices={context.choices} />
      ) : (
        <>
          {context.choices.length > 1 && (
            <Button tone="subtle">
              <Link
                to={`/${sport}/fixtures/new`}
                style={{ color: "inherit", textDecoration: "none" }}
              >
                ← Choose a different kind
              </Link>
            </Button>
          )}
          <FixtureForm context={context} />
        </>
      )}
    </>
  );
}
